import { ApiResponse } from "@/types";
import { apiSlice } from "@/redux/apiSlice";
import { Facility as FacilityType, FacilityTable } from "@/types/facility";
import {
  FacilityBooking,
  FacilityBookingTable,
} from "@/types/facility/booking";
import {
  FacilitySchedule,
  FacilityScheduleSlot,
  FacilityScheduleSlots,
  FacilityScheduleTable,
} from "@/types/facility/schedule";

const userToken = localStorage.getItem("userToken");

export const Facility = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      // Facility Endpoints
      getFacilities: builder.query<
        FacilityTable,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/facility?org_id=${orgId}${query ? `&${query}` : ""}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: (result, error, { orgId }) => [
          { type: "Facility", id: `LIST_${orgId}` },
          "Facility",
          "FacilityCategory",
          "IncomeCategory",
        ],
      }),

      getFacilityById: builder.query<
        FacilityType,
        {
          org_id: number;
          id: number;
          query?: string;
        }
      >({
        query: ({ id, org_id, query }) => ({
          url: `/facility/${id}?org_id=${org_id}${
            query && query.length > 0 ? `&${query}` : ""
          }`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: (result, error, { id, org_id }) => [
          { type: "Facility", id: `${org_id}_${id}` },
          "Facility",
          "FacilityCategory",
          "IncomeCategory",
        ],
        // Immediately drop cache when there are no active subscribers
        keepUnusedDataFor: 0,
      }),

      createFacility: builder.mutation<ApiResponse, FacilityType>({
        query: (facility) => ({
          url: `/facility`,
          method: "POST",
          body: facility,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Facility"],
      }),
      updateFacility: builder.mutation<ApiResponse, FacilityType>({
        query: (facility) => ({
          url: `/facility/${facility.id}`,
          method: "PUT",
          body: facility,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Facility", "FacilityCategory", "IncomeCategory"],
      }),
      deleteFacility: builder.mutation<ApiResponse, number>({
        query: (facility_id) => ({
          url: `/facility/${facility_id}`,
          method: "DELETE",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Facility"],
      }),
      // Schedule Endpoints
      createFacilitySchedule: builder.mutation<ApiResponse, FacilitySchedule>({
        query: (data) => ({
          url: `/facility/schedule`,
          method: "POST",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Facility"],
      }),
      updateFacilitySchedule: builder.mutation<ApiResponse, FacilitySchedule>({
        query: (data) => ({
          url: `/facility/schedule/${data.general_info.id}`,
          method: "PUT",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),
      getFacilityScheduleById: builder.query<
        FacilitySchedule,
        { org_id: number; schedule_id: number; query: string }
      >({
        query: ({ org_id, schedule_id, query }) => ({
          url: `/facility/schedule/${schedule_id}?org_id=${org_id}&${query}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      deleteFacilitySchedule: builder.mutation<ApiResponse, number>({
        query: (schedule_id) => ({
          url: `/facility/schedule/${schedule_id}`,
          method: "DELETE",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),
      getAllFacilitySchedules: builder.query<
        FacilityScheduleTable,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/facility/schedule?org_id=${orgId}${query ? `&${query}` : ""}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      getAllFacilityScheduleSlot: builder.query<
        FacilityScheduleSlots,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/facility/schedule/slots?org_id=${orgId}${
            query ? `&${query}` : ""
          }`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      getFacilityScheduleSlot: builder.query<
        ApiResponse,
        { schedule_id: number }
      >({
        query: ({ schedule_id }) => ({
          url: `/facility/schedule/slots`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      getFacilityScheduleSlotById: builder.query<
        FacilityScheduleSlot,
        { slotId: number; orgId: number; loadAddsOn: boolean }
      >({
        query: ({ slotId, orgId, loadAddsOn }) => ({
          url: `/facility/schedule/slots/${slotId}?org_id=${orgId}&load_add_ons=${loadAddsOn}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      getFacilityList: builder.query<
        { id: string; name: string }[],
        { id: number; query: string }
      >({
        query: ({ id, query }) => ({
          url: `/facility?org_id=${id}${query ? `&${query}` : ""}`,
          headers: {
            Accept: "application/json",
          },
          providesTags: ["Facility", "IncomeCategory"],
        }),
        transformResponse: (response: {
          data: { id: number; name: string }[];
          total_counts: number;
        }) => {
          return response.data.map((item) => ({
            id: String(item.id), // Convert to string since Select expects string values
            name: item.name,
          }));
        },
      }),
      // Booking Endpoints
      createFacilityBooking: builder.mutation<ApiResponse, FacilityBooking>({
        query: (data) => ({
          url: `/bookings`,
          method: "POST",
          body: data,
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),
      updateFacilityBooking: builder.mutation<ApiResponse, FacilityBooking>({
        query: (data) => ({
          url: `/bookings/${data.id}`,
          method: "PUT",
          body: data,
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),
      deleteFacilityBooking: builder.mutation<ApiResponse, number>({
        query: (schedule_id) => ({
          url: `/bookings/${schedule_id}`,
          method: "DELETE",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),
      getAllFacilityBookings: builder.query<
        FacilityBookingTable,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/bookings?org_id=${orgId}${query ? `&${query}` : ""}`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      getFacilityBookingById: builder.query<FacilityBooking, number>({
        query: (id) => ({
          url: `/bookings/${id}`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      // patch swap-member
      swapMember: builder.mutation<
        ApiResponse,
        { booking_id: number; target_member_id: number; org_id: number }
      >({
        query: ({ booking_id, target_member_id, org_id }) => ({
          url: `/bookings/${booking_id}/swap-member`,
          method: "PATCH",
          body: {
            target_member_id,
            org_id,
          },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),
      // BOOKING QR-STATUS
      qrStatus: builder.query<ApiResponse, { booking_id: number }>({
        query: (booking_id) => ({
          url: `/bookings/${booking_id}/qr-status`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      // INVITE MEMBER BOOKING
      inviteMemberBooking: builder.mutation<
        ApiResponse,
        { booking_id: number; member_id: number }
      >({
        query: ({ booking_id, member_id }) => ({
          url: `/bookings/${booking_id}/invite`,
          method: "POST",
          body: {
            member_id: member_id,
          },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),
      updateBookingStatus: builder.mutation<
        ApiResponse,
        {
          booking_id: number;
          action?: "booking_approved" | "booking_rejected";
          payment_action?: string;
          reason?: string;
          org_id: number;
          auth_token?: string;
        }
      >({
        query: ({ booking_id, action, payment_action, reason, org_id, auth_token }) => ({
          url: `/bookings/${booking_id}/update-status`,
          method: "PATCH",
          headers: {
            Accept: "application/json",
            Authorization: auth_token !== "" ? auth_token as string : userToken as string,
          },
          body: { action, payment_action, reason, org_id },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),
      validateBooking: builder.query<
        ApiResponse,
        {
          org_id: number;
          schedule_id: number;
          slot_id: number;
        }
      >({
        query: ({ org_id, schedule_id, slot_id }) => ({
          url: `/bookings/validate-booking-date?org_id=${org_id}&schedule_id=${schedule_id}&slot_id=${slot_id}`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      criteriaValidation: builder.query<
        ApiResponse,
        {
          org_id: number;
          member_id: number;
          facility_id: number;
        }
      >({
        query: ({ org_id, member_id, facility_id }) => ({
          url: `/bookings/criteria-validation?org_id=${org_id}&member_id=${member_id}&facility_id=${facility_id}`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
    };
  },
});

export const {
  useGetFacilitiesQuery,
  useGetFacilityByIdQuery,
  useCreateFacilityMutation,
  useUpdateFacilityMutation,
  useDeleteFacilityMutation,
  // Facility Schedule Endpoints
  useCreateFacilityScheduleMutation,
  useUpdateFacilityScheduleMutation,
  useGetFacilityScheduleByIdQuery,
  useDeleteFacilityScheduleMutation,
  useGetAllFacilitySchedulesQuery,
  useGetFacilityScheduleSlotQuery,
  useGetFacilityListQuery,
  useGetAllFacilityScheduleSlotQuery,
  // Facility Booking Endpoints
  useCreateFacilityBookingMutation,
  useUpdateFacilityBookingMutation,
  useDeleteFacilityBookingMutation,
  useGetAllFacilityBookingsQuery,
  useGetFacilityBookingByIdQuery,
  useSwapMemberMutation,
  useQrStatusQuery,
  useInviteMemberBookingMutation,
  useUpdateBookingStatusMutation,
  useValidateBookingQuery,
  useCriteriaValidationQuery,
  useLazyCriteriaValidationQuery,
} = Facility;
